import { EmployeeSalary } from '../../store/employee-salary/employee-salary.state'
import { IncomingPayroll } from '../../store/payroll/payroll.state'
import { formatDate, getFirstDayOfUTCMonth, getYearMonthString } from '../../utils/payroll.utils'

/**
 * Filters out payrolls whose payoutFrom date is in the future.
 */
export function filterRelevantPayrolls(
  payrolls: IncomingPayroll[],
  payPeriodEndDateStr: string
): IncomingPayroll[] {
  if (!payrolls || payrolls.length === 0) return []

  const payPeriodEndDateObj = new Date(payPeriodEndDateStr + 'T00:00:00Z')
  const currentYear = payPeriodEndDateObj.getUTCFullYear()
  const currentMonth = payPeriodEndDateObj.getUTCMonth() + 1

  return payrolls.filter(ip => {
    const payoutDate = ip.salary.payoutFrom
    if (!payoutDate) return false
    const [payYear, payMonth] = payoutDate.split('-').map(Number)
    if (isNaN(payYear) || isNaN(payMonth)) return false

    return payYear < currentYear || (payYear === currentYear && payMonth <= currentMonth)
  })
}

/**
 * Determines which previous salaries and arrear LOP days need to be fetched.
 */
export function prepareDataLoadRequests(
  filteredPayrolls: IncomingPayroll[],
  prevSalariesMap: Record<string, EmployeeSalary | null> | null,
  arrearLopsMap: Record<string, number> | null,
  payPeriodStartDateStr: string,
  payPeriodEndDateStr: string
) {
  const prevSalaryRequests: { employeeId: string; currentSalaryEffectiveFrom: string }[] = []
  const arrearLopRequests: {
    employeeId: string
    businessId: string
    periodStart: string
    periodEnd: string
  }[] = []

  const payPeriodStartDateObj = new Date(payPeriodStartDateStr + 'T00:00:00Z')
  const payPeriodEndDateObj = new Date(payPeriodEndDateStr + 'T00:00:00Z')
  const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)
  const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)

  filteredPayrolls.forEach(ip => {
    const { salary, employeeId, belongsTo } = ip
    const currentSalaryEffectiveFromObj = new Date(salary.effectiveFrom + 'T00:00:00Z')

    const isArrearsPossible =
      salary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth
    const isProrationPossible =
      salary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
      currentSalaryEffectiveFromObj <= payPeriodEndDateObj
    const needsPrevSalary =
      !prevSalariesMap || !Object.prototype.hasOwnProperty.call(prevSalariesMap, employeeId)

    if ((isArrearsPossible || isProrationPossible) && needsPrevSalary) {
      prevSalaryRequests.push({ employeeId, currentSalaryEffectiveFrom: salary.effectiveFrom })
    }

    const prevSalaryExists =
      prevSalariesMap &&
      Object.prototype.hasOwnProperty.call(prevSalariesMap, employeeId) &&
      prevSalariesMap[employeeId]
    const needsArrearLops =
      !arrearLopsMap || !Object.prototype.hasOwnProperty.call(arrearLopsMap, ip.employeeId)

    if (isArrearsPossible && prevSalaryExists && needsArrearLops) {
      const arrearPeriodEndObj = new Date(firstDayOfCurrentPayPeriodMonth.getTime())
      arrearPeriodEndObj.setUTCDate(arrearPeriodEndObj.getUTCDate() - 1)

      if (new Date(salary.effectiveFrom + 'T00:00:00Z') <= arrearPeriodEndObj) {
        arrearLopRequests.push({
          employeeId,
          businessId: belongsTo,
          periodStart: salary.effectiveFrom,
          periodEnd: formatDate(arrearPeriodEndObj)
        })
      }
    }
  })

  return { prevSalaryRequests, arrearLopRequests }
}
